import { z } from 'zod';

import { CreateLoanRow } from '@willow/types-iso';

import { getParsedFields } from '../CreateLoanCrossFieldParser';

export type ValidateInvestorFieldsOptions = {
  investorReportingEnabled: boolean;
};

export const validateInvestorFields =
  (options: ValidateInvestorFieldsOptions) =>
  (
    data: Pick<
      CreateLoanRow,
      'investorName' | 'investorLoanId' | 'ownerId' | 'productType' | 'fhaCaseNumber' | 'fhaAdpCode'
    >,
    ctx: z.RefinementCtx,
  ) => {
    const parsed = getParsedFields(data, [
      'investorName',
      'investorLoanId',
      'ownerId',
      'productType',
      'fhaCaseNumber',
      'fhaAdpCode',
    ]);

    if (!parsed) {
      return;
    }

    if (parsed.investorName != null && parsed.investorLoanId == null) {
      ctx.addIssue({
        code: 'custom',
        message: 'Loan with `investorName` must have `investorLoanId`',
        path: ['investorName'],
      });
    }

    if (parsed.investorName != null && parsed.ownerId != null) {
      ctx.addIssue({
        code: 'custom',
        message:
          'Loan can only have `investorName` or `ownerId`, not both. Use `investorName` to board Fannie Mae, Freddie Mac, or Ginnie Mae.',
        path: ['ownerId'],
      });
    }

    const normalizedOwnerId = parsed.ownerId != null ? parsed.ownerId.replace(/[\s\-_]/g, '').toLowerCase() : null;

    if (
      normalizedOwnerId &&
      ['fnma', 'fhlmc', 'gnma', 'fanniemae', 'freddiemac', 'ginniemae'].includes(normalizedOwnerId)
    ) {
      ctx.addIssue({
        code: 'custom',
        message: `Use \`investorName\` to board Fannie Mae, Freddie Mac, or Ginnie Mae`,
        path: ['ownerId'],
      });
    }

    // Only validate FHA-specific fields if investor reporting is enabled
    if (options.investorReportingEnabled && parsed.productType === 'fha') {
      if (parsed.fhaCaseNumber == null || String(parsed.fhaCaseNumber).trim() === '') {
        ctx.addIssue({
          code: 'custom',
          message: '`fhaCaseNumber` is required when productType is FHA',
          path: ['productType'],
        });
      }
      if (parsed.fhaAdpCode == null) {
        ctx.addIssue({
          code: 'custom',
          message: '`fhaAdpCode` is required when productType is FHA',
          path: ['productType'],
        });
      }
    }
  };
